import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Car, Phone, MapPin } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Google Maps URL (share link)
  const googleMapsUrl = 'https://share.google/WXGpaFo3CKnHfW9LF';
  
  // Apple Maps URL
  const appleMapsUrl = 'https://maps.apple.com/?address=8203%20Bluff%20Springs%20Rd,%20Unit%204,%20Austin,%20TX%20%2078744,%20United%20States&auid=6145737295385498498&ll=30.166498,-97.769819&lsp=9902&q=We%20Have%20Your%20Car';

  const handleAddressClick = (e) => {
    e.preventDefault();
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check for iOS or Mac -> Apple Maps
    if (/iPad|iPhone|iPod/.test(userAgent) || /Macintosh|MacIntel/.test(navigator.platform)) {
      window.open(appleMapsUrl, '_blank');
    } else {
      // All others (Android, Windows, Linux) -> Google Maps with Place ID
      window.open(googleMapsUrl, '_blank');
    }
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/inventory', label: 'Inventory' },
    { path: '/credit-application', label: 'Apply for Financing' },
    { path: '/referral-program', label: 'Referral Program' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact-us', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-[#0a1628]/95 backdrop-blur-md border-b border-[#1a2d47] sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-[#1a4d8c] py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-xs sm:text-sm">
          <div className="flex items-center gap-3 sm:gap-6 text-white/90">
            <a href="tel:5126660016" className="flex items-center gap-1.5 sm:gap-2 hover:text-white transition-colors" data-testid="phone-link">
              <Phone size={14} className="shrink-0" />
              <span>(512) 666-0016</span>
            </a>
            <a href={googleMapsUrl} onClick={handleAddressClick} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 sm:gap-2 hover:text-white transition-colors cursor-pointer">
              <MapPin size={14} className="shrink-0" />
              <span className="hidden xs:inline">8203 Bluff Springs Rd, Austin, TX</span>
              <span className="xs:hidden">Austin, TX</span>
            </a>
          </div>
          <div className="text-white/90 hidden sm:block">
            Mon-Sat 10AM-6PM
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://res.cloudinary.com/dby6n4go0/image/upload/v1769619796/wehaveyourcar/logo.png" 
              alt="We Have Your Car" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-[#ff6b35]'
                    : 'text-[#a8b8d0] hover:text-white'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link
              to="/credit-application"
              className="btn-accent inline-flex items-center gap-2"
              data-testid="apply-now-btn"
            >
              Apply Now
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-[#1a2d47] animate-fade-in">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block py-3 px-4 font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-[#ff6b35] bg-[#1a2d47]/50'
                    : 'text-[#a8b8d0] hover:text-white hover:bg-[#1a2d47]/30'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-4 px-4">
              <Link
                to="/credit-application"
                onClick={() => setIsOpen(false)}
                className="btn-accent w-full text-center block"
              >
                Apply Now
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
